import {Component, OnInit} from '@angular/core';
import {AlertService} from "../../services";
import {FormArray, FormBuilder, FormGroup, Validators} from '@angular/forms';

import { Router, ActivatedRoute } from '@angular/router';
import { first } from 'rxjs/operators';
import {MatDialog} from "@angular/material/dialog";
import {ComponentTitleService} from "../../services/componentTitle.service";
import {FuseTranslationLoaderService} from "../../../../@fuse/services/translation-loader.service";

import { locale as polish } from './i18n/pl';
import {MenuService} from "./service/menu.service";
import {MenuItem} from "./model/menuItem";
import {Menu} from "./model/menu";
import {DeleteMenuDialogComponent} from "./dialog/deleteMenuDialog.component";
import {CreateMenuItemDialogComponent} from "./dialog/createMenuItemDialog.component";
import {Language} from "../language/model/language";

@Component({
    templateUrl: './menuForm.component.html',
    styleUrls: ['./menuForm.component.scss']
})

export class MenuFormComponent implements OnInit {

    form: FormGroup;
    loading = false;
    submitted = false;
    id: string;
    isAddMode: boolean;
    categories: any[];
    units: any[];
    items: MenuItem[];
    createItemDialogRef: any;
    menu: Menu;
    languages: Language[];

    constructor(
        public _matDialog: MatDialog,
        private formBuilder: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private alertService: AlertService,
        private componentTitleService: ComponentTitleService,
        private translationLoader: FuseTranslationLoaderService,
        private menuService: MenuService,
    ) {
        this.translationLoader.loadTranslations(polish);
        this.languages = this.route.snapshot.data.languages.items;

        componentTitleService.clearTitle();
        componentTitleService.addPart('Menus');
    }

    ngOnInit() {
        this.id = this.route.snapshot.params['id'];
        this.menu = this.route.snapshot.data.menu;
        this.isAddMode = !this.id;

        if (this.isAddMode) {
            this.componentTitleService.addPart('New');
        } else {
            this.componentTitleService.addPart('Edit');
        }

        this.form = this.formBuilder.group({
            name: ['', Validators.required],
            items: this.formBuilder.array([]),
        });

        if (!this.isAddMode) {
            this.f.name.setValue(this.route.snapshot.data.menu.name);

            for (let i=0 ; i<this.route.snapshot.data.menu.items.length; i++) {
                this.addMenuItem();
                this.setMenuItemValue(
                    i,
                    this.route.snapshot.data.menu.items[i].name,
                    this.route.snapshot.data.menu.items[i].tagTitle,
                    this.route.snapshot.data.menu.items[i].order,
                    this.route.snapshot.data.menu.items[i].page
                );
            }
        }
    }

    setMenuItemValue(itemIdx: number, name: string, tagTitle: string, order: number, page: string) {
        this.menuItems().at(itemIdx).get('name').setValue(name);
        this.menuItems().at(itemIdx).get('tagTitle').setValue(tagTitle);
        this.menuItems().at(itemIdx).get('order').setValue(order);
        this.menuItems().at(itemIdx).get('page').setValue(page);
    }

    // convenience getter for easy access to form fields
    get f() { return this.form.controls; }

    onSubmit() {
        this.submitted = true;

        // reset alerts on submit
        this.alertService.clear();

        // stop here if form is invalid
        if (this.form.invalid) {
            return;
        }

        this.loading = true;

        if (this.isAddMode) {
            this.createMenu();
        } else {
            this.updateMenu();
        }
    }

    menuItems(): FormArray {
        return this.form.get('items') as FormArray;
    }

    newMenuItem(): FormGroup {
        return this.formBuilder.group({
            id: [''],
            page: [''],
            name: [''],
            tagTitle: [''],
            order: [''],
        });
    }

    addMenuItem() {
        this.menuItems().push(this.newMenuItem());
    }

    removeMenuItem(menuItemIdx: number) {
        this.menuItems().removeAt(menuItemIdx);
    }

    showCreateMenuItemDialog(menu: Menu, parent: MenuItem|null, currentItem: MenuItem|null) {
        this.createItemDialogRef = this._matDialog.open(CreateMenuItemDialogComponent, {
            panelClass: 'create-menu-item-dialog',
            data      : {
                action : 'show',
                menu: menu,
                parentItem: parent,
                currentItem: currentItem,
            },
            width: '750px',
        });

        this.createItemDialogRef.afterClosed()
            .subscribe((response: FormGroup) => {
                if (!response) {
                    return;
                }

                response.value.page = response.value.page.id;
                response.value.items = [];

                let menu = Object.assign({}, this.menu);
                this.insertItemToMenu(response.value, menu.items);

                this.menuService.update(this.menu.id, menu).pipe(first())
                    .subscribe(res => {
                        window.location.reload();
                    });

                this.createItemDialogRef.close();

            }, (err) => {
                console.log(err);
            });
    }

    private insertItemToMenu(item: MenuItem, items: MenuItem[]) {
        if (item.parent) {
            let added = false;
            for (let i = 0; i < items.length; i++) {
                console.log(i);
                if (items[i].id !== item.parent.id && items[i].items.length>0) {
                    this.insertItemToMenu(item, items[i].items);
                    continue;
                } else if (items[i].id !== item.parent.id) {
                    continue;
                }

                items[i].items.push(item);
                items[i].items = [...items[i].items];
                added = true;
                break;
            }
        } else {
            items.push(item);
        }

        items = [...items];

        return items;
    }

    private createMenu() {
        this.menuService.create(this.form.value)
            .pipe(first())
            .subscribe(
                data => {
                    this.alertService.success('Menu added successfully', { keepAfterRouteChange: true });
                    this.router.navigate(['/cms/menus']);
                },
                error => {
                    this.alertService.error(error);
                    this.loading = false;
                });
    }

    private updateMenu() {
        this.menuService.update(this.id, this.form.value)
            .pipe(first())
            .subscribe(
                data => {
                    this.alertService.success('Update successful', { keepAfterRouteChange: true });
                    this.router.navigate(['/cms/menus']);
                },
                error => {
                    this.alertService.error(error);
                    this.loading = false;
                });
    }
}
